import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  memo,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { Country } from "country-state-city";
import {
  bankAccountFields,
  pfAccountFields,
  panFields,
  aadhaarFields,
  passportFields,
} from "./ProfileData";
import {
  getAccountStationary,
  updateAccountStationary,
} from "../../../ApiHelper/url_Helpers.js";
import Loader from "../../../Components/Loader";
import { RiseLoader } from "react-spinners";

// --- Validation Schema ---
const validationSchema = Yup.object().shape({
  bank_account_number: Yup.string().when([], {
    is: () => true,
    then: (schema) =>
      bankAccountFields.find((f) => f.id === "bank_account_number")?.required
        ? schema.required("Bank account number is required")
        : schema.nullable(),
  }),
  confirm_bank_account_number: Yup.string()
    .oneOf([Yup.ref("bank_account_number"), null], "Account numbers must match")
    .when("bank_account_number", {
      is: (val) => val && val.length > 0,
      then: (schema) => schema.required("Please confirm your account number"),
    }),

  ...[
    ...bankAccountFields.filter(
      (f) =>
        f.id !== "bank_account_number" &&
        f.id !== "confirm_bank_account_number",
    ),
    ...pfAccountFields,
    ...panFields,
    ...aadhaarFields,
    ...passportFields,
  ].reduce((acc, field) => {
    if (field.required) {
      acc[field.id] = Yup.string().required(`${field.label} is required`);
    }
    return acc;
  }, {}),
});

// --- FormField Component ---
const FormField = memo(
  ({ field, name, value, onChange, onBlur, error, touched, readOnly }) => {
    const isError = touched && Boolean(error);
    const selectOptions = useMemo(
      () => field.options?.map((opt) => ({ label: opt, value: opt })) || [],
      [field.options],
    );
    const currentValue = selectOptions.find((o) => o.value === value) || null;

    const customStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: "38px",
        borderRadius: "0.5rem",
        fontSize: "14px",
        backgroundColor: readOnly ? "#F3F4F6" : "#FAFBFD",
        borderColor: isError
          ? "#ef4444"
          : state.isFocused
            ? "#00B8D9"
            : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 1px #00B8D9" : "none",
        "&:hover": { borderColor: state.isFocused ? "#00B8D9" : "#d1d5db" },
        cursor: readOnly ? "not-allowed" : "default",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
      <div className="flex flex-col justify-start p-1.5 md:p-4 gap-1.5 w-full min-w-0">
        <label
          htmlFor={name}
          className="text-[13px] md:text-sm font-semibold text-gray-700"
        >
          {field.label}
          {field.required && !readOnly && (
            <span className="text-red-500 ml-1 font-bold">*</span>
          )}
        </label>
        {field.type === "select" ? (
          <Select
            id={name}
            instanceId={name}
            options={selectOptions}
            value={currentValue}
            isDisabled={readOnly}
            placeholder={`Select ${field.label}...`}
            styles={customStyles}
            isSearchable={true}
            menuPortalTarget={document.body}
            onChange={(selected) =>
              onChange({
                target: { name, value: selected ? selected.value : "" },
              })
            }
            onBlur={() => onBlur({ target: { name } })}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={field.type}
            value={value || ""}
            onChange={onChange}
            onBlur={onBlur}
            readOnly={readOnly}
            placeholder={field.placeholder}
            className={`border rounded-lg px-3 py-2 text-sm w-full outline-none transition-all ${
              readOnly ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
            } ${
              isError
                ? "border-red-500 bg-red-50 ring-1 ring-red-200"
                : "border-gray-300 bg-[#FAFBFD] focus:ring-1 focus:ring-[#00B8D9]"
            }`}
          />
        )}
        <div className="min-h-[16px]">
          {isError && !readOnly && (
            <p className="text-red-500 text-[10px] md:text-xs italic font-medium">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  },
);

const AccoundStationary = forwardRef(({ onEditStatusChange, onDirtyChange }, ref) => {
  const [loading, setLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(false); // Master control for lock/unlock
  const allCountries = useMemo(
    () => Country.getAllCountries().map((c) => c.name),
    [],
  );

  const withDynamicCountries = (fields) =>
    fields.map((f) =>
      f.id.includes("country") && f.type === "select"
        ? { ...f, options: allCountries }
        : f,
    );

  const dynamicBankFields = useMemo(
    () => withDynamicCountries(bankAccountFields),
    [allCountries],
  );
  const dynamicPassportFields = useMemo(
    () => withDynamicCountries(passportFields),
    [allCountries],
  );

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    validationSchema,
  });

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyChange]);

  const fetchDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAccountStationary();
      if (res) {
        // Backend action check
        const canEdit =( res.bank_details?.action?.isEdit  ||  res.statutory_details?.action?.isEdit) ?? false;
        setIsEditable(canEdit);
        onEditStatusChange(canEdit)
        const bankDetails = { ...res.bank_details?.data };
        const statutoryDetails = { ...res.statutory_details?.data };

        if (bankDetails.country) bankDetails.bank_country = bankDetails.country;

        if (bankDetails.account_type) {
          const val = bankDetails.account_type.toString();
          bankDetails.account_type =
            val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
        }
        if (bankDetails.name_as_per_bank)
          bankDetails.bank_account_name = bankDetails.name_as_per_bank;

        formik.setValues({
          ...bankDetails,
          ...statutoryDetails,
          name_in_passport: statutoryDetails.name_in_passport,
          passport_expiry_date: statutoryDetails.passport_expiry_date,
        });
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  /**
   * Parent (MyProfile.js) reads this object via childRef.current
   */
  useImperativeHandle(ref, () => ({
    isEditable: isEditable, // This controls the Save/Cancel buttons in Parent
    handleChildSave: async () => {
      if (!isEditable) return { success: false, error: "Editing is locked" };

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          [
            ...bankAccountFields,
            ...pfAccountFields,
            ...panFields,
            ...aadhaarFields,
            ...passportFields,
          ].reduce((acc, f) => ({ ...acc, [f.id]: true }), {
            confirm_bank_account_number: true,
          }),
        );
        return { success: false, error: "Validation errors" };
      }

      try {
        const payload = { bank_details: {}, statutory_details: {} };

        bankAccountFields.forEach((f) => {
          let val = formik.values[f.id];
          if (val) {
            const apiKey =
              f.id === "bank_country"
                ? "country"
                : f.id === "bank_account_name"
                  ? "name_as_per_bank"
                  : f.id;

            if (f.id === "account_type") val = val.toLowerCase();
            payload.bank_details[apiKey] = val;
          }
        });

        [
          ...pfAccountFields,
          ...panFields,
          ...aadhaarFields,
          ...passportFields,
        ].forEach((f) => {
          if (formik.values[f.id])
            payload.statutory_details[f.id] = formik.values[f.id];
        });

        await updateAccountStationary(payload);

        // Refresh data and keep it locked until next API call confirms status
        await fetchDetails();
        return { success: true };
      } catch (err) {
        return { success: false, error: "Save failed" };
      }
    },
  }));

  return (
    <div className="flex flex-col gap-6 w-full pb-10">
      {loading ? (
        <div
          className={`flex items-center justify-center w-full h-full min-h-[350px] bg-white`}
        >
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        <div>
          <div className="border-2 bg-white rounded-lg pb-4 shadow-sm overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50/50">
              <h3 className="font-bold text-black">Bank Account Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3">
              {dynamicBankFields.map((field) => (
                <FormField
                  key={field.id}
                  field={field}
                  name={field.id}
                  value={formik.values[field.id]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors[field.id]}
                  touched={formik.touched[field.id]}
                  readOnly={!isEditable}
                />
              ))}
            </div>
          </div>

          <div className="border-2 bg-white rounded-lg pb-4 shadow-sm mt-8 overflow-hidden">
            <h3 className="px-5 border-b py-3 font-semibold bg-gray-50/50">
              Statutory Details
            </h3>
            {[
              { title: "PF Account", fields: pfAccountFields },
              { title: "PAN Details", fields: panFields },
              { title: "Aadhaar Details", fields: aadhaarFields },
              { title: "Passport Details", fields: dynamicPassportFields },
            ].map((section, idx) => (
              <React.Fragment key={idx}>
                <h4 className="font-bold text-[11px] px-5 mt-6 text-gray-400 uppercase tracking-widest">
                  {section.title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {section.fields.map((field) => (
                    <FormField
                      key={field.id}
                      field={field}
                      name={field.id}
                      value={formik.values[field.id]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors[field.id]}
                      touched={formik.touched[field.id]}
                      readOnly={!isEditable}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default AccoundStationary;
